import api from "@api"
import { useStore } from "@stores"
import hasAccess from "@api/hasAccess"
import getImgixUrl from "@helpers/getImgixUrl"
import { useAppContext } from "@components/AppContext"
import getResizedImageUrl from "@helpers/getResizedImageUrl"
import { upload, readFileContent } from "@specs/storage"

export const readOperation =  api.get("campaigns/ReadCampaign")
export const indexOperation = api.get("campaigns/IndexCampaigns")
export const cloneOperation = api.get("campaigns/CloneCampaign")
export const deleteOperation = api.get("campaigns/DeleteCampaign")
export const createOperation = api.get("campaigns/CreateCampaign")
export const updateOperation = api.get("campaigns/UpdateCampaign")
export const publishOperation = api.get("campaigns/PublishCampaign")
export const unpublishOperation = api.get("campaigns/UnpublishCampaign")
export const updateTagsOperation = api.get("campaigns/UpdateCampaignTags")
export const updateCampaignTagsOperation = api.get("campaigns/UpdateCampaignTags")

const HEIC_MIMETYPE = "image/heic"

const useCampaignsStore = () => {
  const { request, identity } = useAppContext()
  const { id: organizationId } = identity.organization

  const canReadCampaigns = hasAccess(["campaigns-read"])
  const shouldAutoLoad = canReadCampaigns

  const {
    items: campaigns,
    getItem: getCampaign,
    addItem: addCampaign,
    removeItem: removeCampaign,
    updateItem: updateCampaign,
    indexItems: indexCampaigns,
  } = useStore(indexOperation.id, request, shouldAutoLoad, {}, [ organizationId ])

  const deleteCampaign = id =>
    request(deleteOperation.id, { id })
      .then(() => removeCampaign(id))

  const tagCampaign = (id, tagId) => {
    const campaign = getCampaign(id)
    const { tagIds = [] } = campaign
    const mutation = { tagIds: [ ...tagIds, tagId ] }

    return request(updateCampaignTagsOperation.id, { id, mutation })
      .then(({ data }) => updateCampaign({ ...campaign, ...data }))
  }

  const untagCampaign = (id, droppedTagId) => {
    const campaign = getCampaign(id)
    const { tagIds = [] } = campaign
    const mutation = { tagIds: [ ...tagIds.filter(tagId => tagId !== droppedTagId) ] }

    return request(updateCampaignTagsOperation.id, { id, mutation })
      .then(({ data }) => updateCampaign({ ...campaign, ...data }))
  }

  const uploadCampaignAssets = async (campaign, files, getRandomId, title, type) => {
    const isImage = type === "IMAGE"

    const newAssets = []

    for (const file of files) {
      const assetId = getRandomId()
      const content = await readFileContent(file)

      const {
        type: mimeType,
        name,
        size,
        lastModified
      } = file

      const isPublic = true

      const { url } = await upload(request, isPublic, mimeType, content)

      const isHeicImage = mimeType === HEIC_MIMETYPE
      const imageUrl = isHeicImage ? getImgixUrl(url, { q: 30 }) : url

      const asset = {
        id: assetId,
        type,
        url: imageUrl,
        size,
        name,
        mimeType,
        lastModified,
      }

      if (title) {
        asset.title = title
      }

      if (isImage) {
        asset.thumb = getResizedImageUrl(url, 200, 120)
      }

      newAssets.push(asset)
    }

    const { id, assets = [] } = campaign
    const mutation = { assets: [ ...newAssets, ...assets ] }
    const parameters = { id, mutation }

    const updatedCampaign = await request(updateOperation.id, parameters)
      .then(({ data }) => {
        updateCampaign(data)
        return data
      })

    return [ newAssets, updatedCampaign ]
  }

  return {
    campaigns,
    addCampaign,
    getCampaign,
    tagCampaign,
    untagCampaign,
    indexCampaigns,
    updateCampaign,
    deleteCampaign,
    uploadCampaignAssets,
  }
}

export default useCampaignsStore
